import React, { createContext, useContext, useState, useCallback } from 'react';

const SubscriptionPricingContext = createContext();

export const useSubscriptionPricing = () => {
  const context = useContext(SubscriptionPricingContext);
  if (!context) {
    throw new Error('useSubscriptionPricing must be used within SubscriptionPricingProvider');
  }
  return context;
};

const DEFAULT_PRICING_TIERS = {
  ONE_TIME_PURCHASE: {
    discountPercentage: 15,
    refundPercentage: 100,
    description: 'Full upfront payment with discount'
  },
  SUBSCRIPTION_NO_CANCELLATION: {
    discountPercentage: 5,
    refundPercentage: 0,
    description: 'Locked-in subscription'
  },
  SUBSCRIPTION_CANCELLATION_ALLOWED: {
    discountPercentage: 0,
    refundPercentage: 30,
    description: 'Flexible subscription with cancellation'
  }
};

export const SubscriptionPricingProvider = ({ children }) => {
  const [pricingTiers, setPricingTiers] = useState(DEFAULT_PRICING_TIERS);
  const [entityOverrides, setEntityOverrides] = useState({});
  
  // Store entity metadata (id -> {name, description, etc.})
  const [entityMetadata, setEntityMetadata] = useState({});
  
  const [pricingStrategy, setPricingStrategy] = useState({
    moduleType: null,
    subscriptionType: null,
    strategy: null,
    appliedToEntities: []
  });
  const [loading, setLoading] = useState(false);

  const storeEntityMetadata = useCallback((entityId, metadata) => {
    setEntityMetadata(prev => ({
      ...prev,
      [entityId]: metadata
    }));
  }, []);

  const getEntityMetadata = useCallback((entityId) => {
    return entityMetadata[entityId] || null;
  }, [entityMetadata]);

  const getEntityName = useCallback((entityId) => {
    const metadata = entityMetadata[entityId];
    return metadata?.name || metadata?.title || entityId;
  }, [entityMetadata]);

  const getEntityDescription = useCallback((entityId) => {
    const metadata = entityMetadata[entityId];
    return metadata?.description || '';
  }, [entityMetadata]);

  const getEntityType = useCallback((entityId) => {
    const metadata = entityMetadata[entityId];
    return metadata?.entityType || null;
  }, [entityMetadata]);

  // ==================== GETTER FUNCTIONS ====================
  const getGlobalPricing = useCallback((subscriptionType) => {
    return pricingTiers[subscriptionType] || null;
  }, [pricingTiers]);

  const getEntityOverride = useCallback((entityId, subscriptionType) => {
    return entityOverrides[entityId]?.[subscriptionType] || null;
  }, [entityOverrides]);

  // const getAllOverrides = useCallback((subscriptionType) => {
  //   const overrides = {};
    
  //   Object.keys(entityOverrides).forEach(entityId => {
  //     const override = entityOverrides[entityId]?.[subscriptionType];
  //     if (override) {
  //       overrides[entityId] = {
  //         ...override,
  //         entityName: getEntityName(entityId),
  //         entityMetadata: getEntityMetadata(entityId)
  //       };
  //     }
  //   });
    
  //   return overrides;
  // }, [entityOverrides, getEntityName, getEntityMetadata]);

  const getEffectivePricing = useCallback((entityId, subscriptionType) => {
    const override = getEntityOverride(entityId, subscriptionType);
    if (override) {
      return {
        ...override,
        source: 'entity_override',
        entityId,
        entityName: getEntityName(entityId)
      };
    }

    const global = getGlobalPricing(subscriptionType);
    return {
      ...global,
      source: 'global',
      subscriptionType
    };
  }, [getEntityOverride, getGlobalPricing, getEntityName]);

  const getEntitiesWithOverrides = useCallback((subscriptionType) => {
    const entities = [];
    
    Object.keys(entityOverrides).forEach(entityId => {
      const override = entityOverrides[entityId]?.[subscriptionType];
      if (override) {
        entities.push({
          entityId,
          ...override,
          entityName: getEntityName(entityId),
          entityMetadata: getEntityMetadata(entityId)
        });
      }
    });
    
    return entities;
  }, [entityOverrides, getEntityName, getEntityMetadata]);

  const getFirstCustomOverride = useCallback((subscriptionType) => {
    for (const entityId in entityOverrides) {
      const override = entityOverrides[entityId]?.[subscriptionType];
      if (override) {
        return {
          entityId,
          ...override,
          entityName: getEntityName(entityId)
        };
      }
    }
    return null;
  }, [entityOverrides, getEntityName]);

  // ==================== SETTER FUNCTIONS ====================
  const updateGlobalPricing = useCallback((subscriptionType, pricingData) => {
    setPricingTiers(prev => ({
      ...prev,
      [subscriptionType]: {
        ...prev[subscriptionType],
        ...pricingData
      }
    }));
  }, []);
  
  const setEntityOverride = useCallback((entityId, subscriptionType, overrideData) => {
    setEntityOverrides(prev => {
      const updated = { ...prev };

      if (!overrideData) {
        if (updated[entityId]) {
          delete updated[entityId][subscriptionType];
          if (Object.keys(updated[entityId]).length === 0) {
            delete updated[entityId];
          }
        }
      } else {
        if (!updated[entityId]) {
          updated[entityId] = {};
        }
        updated[entityId][subscriptionType] = overrideData;
      }

      return updated;
    });
  }, []);

  const getAllOverrides = useCallback((subscriptionType) => {
    const overrides = {};
    
    Object.keys(entityOverrides).forEach(entityId => {
      const override = entityOverrides[entityId]?.[subscriptionType];
      if (override) {
        overrides[entityId] = {
          ...override,
          entityName: getEntityName(entityId),
          entityMetadata: getEntityMetadata(entityId)
        };
      }
    });
    
    return overrides;
  }, [entityOverrides, getEntityName, getEntityMetadata]);

  const setMultipleEntityOverrides = useCallback((entityIds, subscriptionType, overrideData) => {
    setEntityOverrides(prev => {
      const updated = { ...prev };

      entityIds.forEach(entityId => {
        if (!updated[entityId]) {
          updated[entityId] = {};
        }
        updated[entityId][subscriptionType] = overrideData;
      });

      return updated;
    });
  }, []);

  const setPricingStrategyConfig = useCallback((config) => {
    setPricingStrategy(prev => ({
      ...prev,
      ...config
    }));
  }, []);

  const resetEntityOverridesForSubscription = useCallback((subscriptionType) => {
    setEntityOverrides(prev => {
      const updated = { ...prev };

      Object.keys(updated).forEach(entityId => {
        delete updated[entityId][subscriptionType];
        if (Object.keys(updated[entityId]).length === 0) {
          delete updated[entityId];
        }
      });

      return updated;
    });
  }, []);

  const clearAllOverrides = useCallback(() => {
    setEntityOverrides({});
  }, []);

  // ==================== UTILITY FUNCTIONS ====================
  const calculatePrice = useCallback((basePrice, subscriptionType, entityId = null, durationMultiplier = 1) => {
    if (!basePrice || !subscriptionType) return null;

    const effectivePricing = entityId
      ? getEffectivePricing(entityId, subscriptionType)
      : getGlobalPricing(subscriptionType);

    if (!effectivePricing) return null;

    const { discountPercentage = 0 } = effectivePricing;
    const discountAmount = basePrice * (discountPercentage / 100);
    const priceAfterDiscount = basePrice - discountAmount;
    const finalPrice = priceAfterDiscount * durationMultiplier;

    return {
      basePrice,
      discountPercentage,
      discountAmount,
      priceAfterDiscount,
      durationMultiplier,
      finalPrice,
      effectivePricing
    };
  }, [getEffectivePricing, getGlobalPricing]);

  const getEntitiesWithPricing = useCallback((entities, subscriptionType) => {
    return entities.map(entity => ({
      ...entity,
      pricing: getEffectivePricing(entity.id, subscriptionType),
      hasOverride: !!getEntityOverride(entity.id, subscriptionType)
    }));
  }, [getEffectivePricing, getEntityOverride]);

  const hasCustomDiscount = useCallback((subscriptionType) => {
    return getFirstCustomOverride(subscriptionType) !== null;
  }, [getFirstCustomOverride]);

  const value = {
    // Pricing data
    pricingTiers,
    entityOverrides,
    pricingStrategy,

    entityMetadata,
    storeEntityMetadata,
    getEntityMetadata,
    getEntityName,
    getEntityDescription,
    getEntityType,

    // Getters
    getGlobalPricing,
    getEntityOverride,
    getAllOverrides,
    getEntitiesWithOverrides,
    getFirstCustomOverride,
    getEffectivePricing,
    getEntitiesWithPricing,

    // Setters
    updateGlobalPricing,
    setEntityOverride,
    setMultipleEntityOverrides,
    setPricingStrategyConfig,
    resetEntityOverridesForSubscription,
    clearAllOverrides,

    // Checkers
    hasCustomDiscount,

    // Utilities
    calculatePrice,
    loading
  };

  return (
    <SubscriptionPricingContext.Provider value={value}>
      {children}
    </SubscriptionPricingContext.Provider>
  );
};
 
 // const setEntityOverride = useCallback((entityId, subscriptionType, overrideData) => {
  //   setEntityOverrides(prev => {
  //     const updated = { ...prev };

  //     if (!overrideData) {
  //       if (updated[entityId]) {
  //         delete updated[entityId][subscriptionType];
  //         if (Object.keys(updated[entityId]).length === 0) {
  //           delete updated[entityId];
  //         }
  //       }
  //     } else {
  //       if (!updated[entityId]) {
  //         updated[entityId] = {};
  //       }
  //       updated[entityId][subscriptionType] = overrideData;
  //     }

  //     return updated;
  //   });
  // }, []);